import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';
import {EditorState, ContentState} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import {withRouter} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {FileAssetsDropZone, FileAssetsInput} from '@ace-de/eua-file-assets';
import {alfMessageChannelTypes, alfMessageTypes, alfMessageTextTemplateTypes, alfOutgoingMessageCategoryTypes, etmTaskTypes, etmLeistTaskCategoryTypes, alfInvoiceChannelTypes} from '@ace-de/eua-entity-types'; // eslint-disable-line max-len
import {useStyles, ContentBlock, ContentItem, HighlightCircle} from '@ace-de/ui-components';
import {SelectField, Option, InputField, RichTextAreaField} from '@ace-de/ui-components/form';
import {Icon, deleteIcon, infoAlertIcon, fileIcon, uploadIcon, plusIcon} from '@ace-de/ui-components/icons';
import * as communicationActionTypes from '../../communication/communicationActionTypes';
import * as invoiceSelectors from '../../invoices/invoiceSelectors';
import * as taskActionTypes from '../../tasks/taskActionTypes';
import * as contactSelectors from '../contactSelectors';
import categoryToTextTemplatesMap from '../../communication/categoryToTextTemplatesMap';
import config from '../../config';
import * as userProfileSelectors from '../../user-profiles/userProfileSelectors';
import useSendMessageForm from '../../communication/hooks/useSendMessageForm';
import EmailFormFields from '../../communication/ui-elements/EmailFormFields';
import MailFormFields from '../../communication/ui-elements/MailFormFields';
import CategoryTemplateSection from '../../communication/ui-elements/CategoryTemplateSection';
import TaskSection from '../../communication/ui-elements/TaskSection';
import styles from '../../communication/modals/CommunicationModals.module.scss';

const SendMessageContent = props => {
    const {cx} = useStyles(props, styles);
    const {serviceCase, serviceCaseInvoices, accountPartyContactDetails, invoice, senderAccounts} = props;
    const {accountPartyContactDetailsFetchedAt, resetAccountPartyContactDetails} = props;
    const {confirmSendMessage, fetchAccountPartyDetails, serviceCaseInvoice} = props;
    const {createTask, activeUser, persistenceState, selectedSearchContact} = props;
    const {createTranslateShorthand, activeLocale, translate} = useTranslate();
    const translateModal = createTranslateShorthand('send_message_modal');

    const form = useSendMessageForm({
        serviceCase,
        accountPartyContactDetails,
        accountPartyContactDetailsFetchedAt,
        fetchAccountPartyDetails,
        resetAccountPartyContactDetails,
        persistenceState,
        invoice,
        selectedSearchContact,
        serviceCaseInvoices,
        translateOtherLabel: translateModal('recipient_option_label.other'),
    });

    const {
        richTextAreaContent, setRichTextAreaContent,
        selectedRecipient, selectedChannel,
        mailFormData, emailFormData,
        subject, setSubject,
        selectedCategory, setSelectedCategory,
        selectedTemplate, setSelectedTemplate,
        isTaskCreationEnabled, setIsTaskCreationEnabled,
        dueDate, setDueDate,
        dueTime, setDueTime,
        name, setName,
        isDueTimeValid, setIsDueTimeValid,
        setHasEmailError,
        isSendingDisabled, isSendingInProgress,
        recipients,
        handleRecipientSelect, handleChannelSelection,
        handleEmailFormChange, handleMailFormChange,
        buildContactAddress,
        fileList, deleteFile,
        isRecipientDataLoading,
    } = form;

    // -- Template text formatting (Content-specific: plain text) --
    const createArgumentsArray = (category, template) => {
        const approvedAmounts = (invoice?.lines || serviceCaseInvoice?.lines)
            ?.map(invoiceLine => invoiceLine.approvedAmount);
        const totalAmount = approvedAmounts?.length
            ? `${approvedAmounts.reduce((currentSum, amount) => currentSum + parseFloat(amount)) || 0}${translate('global.currency.euro')}`
            : '-';

        switch (category) {
            case alfOutgoingMessageCategoryTypes.OTHER: {
                switch (template) {
                    case alfMessageTextTemplateTypes.REGRESS_INSURANCE: {
                        return {
                            memberName: `${serviceCase.member?.personalDetails?.displayName || '-'}`,
                            serviceCaseId: `${serviceCase.prefix}-${serviceCase.id}`,
                            totalAmount,
                        };
                    }
                    case alfMessageTextTemplateTypes.REQUEST_POLICE_OR_PUBLIC_PROSECUTOR_BLOOD_ALCOHOL: {
                        return {
                            memberName: `${serviceCase.member?.personalDetails?.displayName || '-'}`,
                        };
                    }
                    default: {
                        return {};
                    }
                }
            }
            case alfOutgoingMessageCategoryTypes.REFUND:
            case alfOutgoingMessageCategoryTypes.REPAYMENT: {
                return {
                    serviceApprovedAmounts: (invoice?.lines?.length || serviceCaseInvoice?.lines?.length) ? `\n\n${(invoice || serviceCaseInvoice).lines.map(line => (
                        `- ${translate(`global.service_type.${line.service.toLowerCase()}`)}: ${line.approvedAmount || 0}${translate('global.currency.euro')}`
                    )).join('\n')}\n\n` : '\n\n',
                    totalAmount,
                };
            }
            default: {
                return {};
            }
        }
    };

    const formatMessageText = (category, template) => {
        if (!category || !template) return;

        if (!categoryToTextTemplatesMap[category].includes(template)) {
            setRichTextAreaContent(EditorState.createEmpty());
            return;
        }

        const argsArray = createArgumentsArray(category, template);
        let firstParagraph;
        let thirdParagraph;

        if (![alfOutgoingMessageCategoryTypes.ELLA, alfOutgoingMessageCategoryTypes.OTHER].includes(category)) {
            firstParagraph = translateModal.apply(this, [`first_paragraph.${category.toLowerCase()}`, {...argsArray}]);
            thirdParagraph = translateModal(`third_paragraph.${category.toLowerCase()}`);
        }

        const secondParagraph = translateModal.apply(this, [
            `second_paragraph.${category.toLowerCase()}_${template.toLowerCase()}`,
            {...argsArray},
        ]);

        // ToDo: remove replaceAll logic if '\n' symbol can be added to translation json files.
        const formattedText = `${firstParagraph ? `${firstParagraph}\n\n` : ''}${secondParagraph.replaceAll('&', '\n\n') || ''}${thirdParagraph ? `\n\n${thirdParagraph}` : ''}`;
        setRichTextAreaContent(EditorState.createWithContent(ContentState.createFromText(formattedText)));
    };

    // -- Category/Template change handlers --
    const handleCategoryChange = value => {
        setSelectedCategory(value);
        setRichTextAreaContent(EditorState.createEmpty());
        setSubject(translate(translateModal(`communication_type_option.${value.toLowerCase()}`)));
        !categoryToTextTemplatesMap[value].includes(selectedTemplate)
            ? setSelectedTemplate('')
            : setSelectedTemplate(selectedTemplate);
    };

    const handleTemplateChange = value => {
        setSelectedTemplate(value);
        formatMessageText(selectedCategory, value);
    };

    // -- Send handler --
    const handleOnMessageSend = () => {
        const html = stateToHTML(richTextAreaContent.getCurrentContent());
        const contactAddress = buildContactAddress();
        const recipientType = recipients[selectedRecipient]?.recipientType;

        const messageData = {
            recipient: name || recipients[selectedRecipient]?.name || '',
            channel: selectedChannel,
            ...(selectedChannel === alfMessageChannelTypes.EMAIL
                ? {
                    toEmail: emailFormData?.toEmail,
                    senderAccountType: emailFormData?.sender,
                }
                : {}
            ),
            ...(contactAddress
                ? {toAddress: contactAddress}
                : {}
            ),
            subject,
            content: html,
            contactId: recipientType === 'MEMBER'
                ? serviceCase?.member?.membershipNo
                : recipientType === 'PRESELECTED_ACCOUNT_PARTY' || recipientType === 'ACCOUNT_PARTY'
                    ? recipients[selectedRecipient].id
                    : '',
            ...(invoice ? {invoiceId: invoice.id} : {}),
            serviceCaseId: serviceCase?.id,
            messageType: alfMessageTypes.OUTGOING,
            attachments: [...fileList.fileAssets.values()],
        };
        confirmSendMessage(messageData);

        if (isTaskCreationEnabled && dueDate && dueTime) {
            let category;

            switch (selectedCategory) {
                case alfOutgoingMessageCategoryTypes.REQUEST: {
                    category = etmLeistTaskCategoryTypes.REQUEST;
                    break;
                }
                case alfOutgoingMessageCategoryTypes.REPAYMENT: {
                    category = etmLeistTaskCategoryTypes.AREAS_OF_CONTRIBUTION;
                    break;
                }
                default: {
                    category = etmLeistTaskCategoryTypes.TASK;
                }
            }

            const taskData = {
                category,
                description: translateModal('task_body.description', {
                    channel: translate(`global.communication_channel_option.${selectedChannel.toLowerCase()}`),
                    addressee: (selectedRecipient === 'OTHER' ? name : recipients[selectedRecipient]?.name) || '',
                    subject,
                }),
                dueDate: moment(`${moment(dueDate).format('YYYY-MM-DD')}T${dueTime}`),
                assignedCaseId: serviceCase?.id,
                memberName: serviceCase?.member.personalDetails.displayName,
                assignedTo: activeUser.id,
                assignedToUser: activeUser.id,
                assignedToRole: false,
                taskType: etmTaskTypes.LEA,
                ...(invoice && {invoiceId: invoice.id}),
            };

            createTask({taskData});
        }
    };

    if (!serviceCase) return null;

    return (
        <ContentBlock className={cx('global!ace-u-full-width')}>
            <ContentItem
                className={cx([
                    'ace-c-content-item--span-3',
                    'global!ace-u-flex',
                    'global!ace-u-flex--direction-column',
                ])}
            >
                <SelectField
                    name="recipient"
                    label={translateModal('select_field_label.recipient')}
                    onChange={handleRecipientSelect}
                    value={selectedRecipient}
                    className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                    isDisabled={isSendingInProgress || isRecipientDataLoading}
                >
                    {recipients && Object.keys(recipients).map((recipient, idx) => {
                        return (
                            <Option
                                name={`recipientOption${idx}`}
                                value={recipient}
                                key={recipients[recipient].id}
                            >
                                {recipients[recipient].name}
                            </Option>
                        );
                    })}
                </SelectField>
                {selectedRecipient === 'OTHER' && (
                    <InputField
                        name="name"
                        label={`${translateModal('input_field_label.name')}*`}
                        className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                        value={name || ''}
                        onChange={setName}
                        isDisabled={isSendingInProgress}
                    />
                )}
                <SelectField
                    className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                    name="channel"
                    label={translateModal('select_field_label.channel')}
                    value={selectedChannel}
                    onChange={value => handleChannelSelection(value)}
                    isDisabled={isSendingInProgress}
                >
                    {Object.values(alfMessageChannelTypes).filter(channel => (
                        channel !== alfMessageChannelTypes.ELLA
                            || (channel === alfMessageChannelTypes.ELLA
                                && invoice?.channel === alfInvoiceChannelTypes.ELLA)
                    )).map(channel => (
                        <Option
                            key={channel}
                            value={channel}
                            name={`channelOption${channel}`}
                        >
                            {translate(`global.communication_channel_option.${channel.toLowerCase()}`)}
                        </Option>
                    ))}
                </SelectField>
                {selectedChannel === alfMessageChannelTypes.EMAIL && (
                    <EmailFormFields
                        emailFormData={emailFormData}
                        onFieldChange={handleEmailFormChange}
                        senderAccounts={senderAccounts}
                        isDisabled={isSendingInProgress}
                        isToEmailDisabled={isRecipientDataLoading}
                        onEmailValidation={setHasEmailError}
                    />
                )}
                {selectedChannel === alfMessageChannelTypes.MAIL && (
                    <MailFormFields
                        mailFormData={mailFormData}
                        onFieldChange={handleMailFormChange}
                        isDisabled={isSendingInProgress || isRecipientDataLoading}
                    />
                )}
                <CategoryTemplateSection
                    selectedCategory={selectedCategory}
                    selectedTemplate={selectedTemplate}
                    selectedChannel={selectedChannel}
                    isDisabled={isSendingInProgress}
                    onCategoryChange={handleCategoryChange}
                    onTemplateChange={handleTemplateChange}
                />
            </ContentItem>
            <ContentItem
                className={cx([
                    'ace-c-content-item--span-9',
                    'global!ace-u-flex',
                    'global!ace-u-flex--direction-column',
                ])}
            >
                <InputField
                    className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-32')}
                    name="subject"
                    label={translateModal('input_field_label.subject')}
                    value={subject || ''}
                    onChange={setSubject}
                    isDisabled={isSendingInProgress}
                />
                <RichTextAreaField
                    name="content"
                    label={translateModal('text_area_field_label.message')}
                    maxLength={config.MESSAGE_MAX_LENGTH}
                    setEditorState={value => {
                        setRichTextAreaContent(value);
                    }}
                    editorState={richTextAreaContent}
                    className={cx('global!ace-u-full-width', 'global!ace-u-margin--bottom-16')}
                    contentClassName={cx('ace-c-text-area--medium')}
                    locale={activeLocale}
                    isResizable={false}
                    placeholder={translateModal('text_area_field_placeholder.enter_message')}
                    isDisabled={isSendingInProgress}
                    shouldFocus={false}
                />
                <ContentBlock>
                    <ContentItem className={cx('ace-c-content-item--span-8')}>
                        <div
                            className={cx([
                                'global!ace-u-grid',
                                'global!ace-u-height--full',
                            ])}
                        >
                            {[...fileList.fileAssets.values()].length > 0
                                && (
                                    <div
                                        className={cx([
                                            'global!ace-u-grid-column--span-5',
                                            'ace-c-communication-modal__file-list',
                                        ])}
                                    >
                                        {[...fileList.fileAssets.values()].map((fileAsset, index) => (
                                            <div
                                                key={fileAsset.id}
                                                className={cx([
                                                    'global!ace-u-padding--16',
                                                    'global!ace-u-grid',
                                                    'global!ace-u-box-skin--pending',
                                                ], {
                                                    'global!ace-u-margin--top-16': index !== 0,
                                                })}
                                            >
                                                <Icon
                                                    icon={fileIcon}
                                                />
                                                <p
                                                    className={cx([
                                                        'global!ace-u-grid-column--span-7',
                                                        'ace-c-communication-modal__file-name',
                                                    ])}
                                                >
                                                    {fileAsset.fileName}
                                                </p>
                                                <Icon
                                                    onClick={() => deleteFile(
                                                        fileAsset,
                                                        config.SHOULD_SEND_DOCUMENT_DELETE_REQUEST,
                                                    )}
                                                    icon={deleteIcon}
                                                    className={cx([
                                                        'ace-c-icon--xs',
                                                        'ace-c-icon--color-medium-emphasis',
                                                    ])}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            <div
                                className={cx([
                                    'global!ace-u-width--full',
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--direction-column',
                                ], {
                                    'global!ace-u-grid-column--span-7': [...fileList.fileAssets.values()].length > 0,
                                    'global!ace-u-grid-column--span-12': [...fileList.fileAssets.values()].length === 0,
                                })}
                            >
                                <FileAssetsDropZone
                                    service="alfFileAssetsService"
                                    fileList="service-case-incoming-messages"
                                    className={cx([
                                        'global!ace-u-padding--40',
                                        'global!ace-u-width--full',
                                        'global!ace-u-height--full',
                                        'global!ace-u-flex',
                                        'global!ace-u-flex--direction-column',
                                        'global!ace-u-flex--justify-center',
                                        'global!ace-u-flex--align-center',
                                    ])}
                                    isDisabled={isSendingInProgress}
                                >
                                    <HighlightCircle
                                        className={cx([
                                            'global!ace-u-height--64',
                                            'global!ace-u-width--64',
                                            'ace-c-highlight-circle--primary-highlight',
                                        ])}
                                    >
                                        <Icon
                                            icon={uploadIcon}
                                            className={cx([
                                                'ace-c-icon--l',
                                            ])}
                                        />
                                    </HighlightCircle>
                                    <div
                                        className={cx([
                                            'global!ace-u-margin--top-16',
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--direction-column',
                                            'global!ace-u-flex--align-center',
                                        ])}
                                    >
                                        <p className={cx('global!ace-u-typography--variant-body-bold')}>
                                            {translateModal('file_drop_zone_title.document_upload')}
                                        </p>
                                        <FileAssetsInput
                                            service="alfFileAssetsService"
                                            fileList="service-case-incoming-messages"
                                            className={cx([
                                                'global!ace-u-margin--16-0',
                                                'ace-c-file-input--no-chrome',
                                            ])}
                                            isDisabled={isSendingInProgress}
                                        >
                                            <div
                                                className={cx([
                                                    'global!ace-u-flex',
                                                    'global!ace-u-flex--align-center',
                                                    'global!ace-u-flex--justify-center',
                                                    'global!ace-u-cursor--pointer',
                                                ])}
                                            >
                                                <Icon
                                                    icon={plusIcon}
                                                    className={cx([
                                                        'global!ace-u-margin--right-8',
                                                        'ace-c-icon--color-highlight-pressed',
                                                    ])}
                                                />
                                                <p
                                                    className={cx([
                                                        'global!ace-u-typography--variant-highlight',
                                                        'global!ace-u-typography--color-higlighted-active',
                                                        'global!ace-u-typography--align-center',
                                                    ])}
                                                >
                                                    {translateModal('file_drop_zone_label.search_documents')}
                                                </p>
                                            </div>
                                        </FileAssetsInput>
                                    </div>
                                </FileAssetsDropZone>
                                {fileList.errors.length > 0 && (
                                    <div className={cx('global!ace-u-margin--8-0')}>
                                        {fileList.errors.map((errorInfo, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className={cx([
                                                        'global!ace-u-flex',
                                                        'global!ace-u-flex--align-center',
                                                        'global!ace-u-margin--4-0',
                                                        'global!ace-u-typography--variant-caption',
                                                        'global!ace-u-typography--color-warning',
                                                    ])}
                                                >
                                                    <Icon
                                                        icon={infoAlertIcon}
                                                        className={cx([
                                                            'global!ace-u-margin--right-4',
                                                            'ace-c-icon--s',
                                                            'ace-c-icon--color-warning',
                                                        ])}
                                                    />
                                                    {translateModal('upload_error.message')}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ContentItem>
                    <ContentItem
                        className={cx([
                            'ace-c-content-item--span-4',
                            'global!ace-u-flex',
                            'global!ace-u-flex--direction-column',
                        ])}
                    >
                        <TaskSection
                            isTaskCreationEnabled={isTaskCreationEnabled}
                            setIsTaskCreationEnabled={setIsTaskCreationEnabled}
                            dueDate={dueDate}
                            setDueDate={setDueDate}
                            dueTime={dueTime}
                            setDueTime={setDueTime}
                            isDueTimeValid={isDueTimeValid}
                            setIsDueTimeValid={setIsDueTimeValid}
                            isSendingDisabled={isSendingDisabled}
                            isSendingInProgress={isSendingInProgress}
                            onSend={handleOnMessageSend}
                        />
                    </ContentItem>
                </ContentBlock>
            </ContentItem>
        </ContentBlock>
    );
};

SendMessageContent.propTypes = {
    serviceCase: PropTypes.object,
    serviceCaseInvoices: PropTypes.arrayOf(PropTypes.object),
    confirmSendMessage: PropTypes.func.isRequired,
    fetchAccountPartyDetails: PropTypes.func.isRequired,
    resetAccountPartyContactDetails: PropTypes.func.isRequired,
    createTask: PropTypes.func.isRequired,
    senderAccounts: PropTypes.array,
    accountPartyContactDetails: PropTypes.object,
    accountPartyContactDetailsFetchedAt: PropTypes.number,
    invoice: PropTypes.object,
    serviceCaseInvoice: PropTypes.object,
    activeUser: PropTypes.object,
    selectedSearchContact: PropTypes.object,
    persistenceState: PropTypes.string.isRequired,
};

SendMessageContent.defaultProps = {
    serviceCaseInvoices: [],
    senderAccounts: [],
    accountPartyContactDetails: null,
    accountPartyContactDetailsFetchedAt: 0,
    invoice: null,
    serviceCase: null,
    serviceCaseInvoice: null,
    activeUser: null,
    selectedSearchContact: null,
};

const mapStateToProps = (state, props) => {
    const serviceCaseInvoicesSelector = invoiceSelectors.createServiceCaseInvoicesSelector();
    const createServiceCaseInvoiceSelector = invoiceSelectors.createServiceCaseInvoiceSelector();
    const getSelectedContact = contactSelectors.createContactSelector();

    return {
        serviceCaseInvoices: serviceCaseInvoicesSelector(state, props),
        senderAccounts: state.communication.senderAccounts,
        accountPartyContactDetails: state.communication.accountPartyContactDetails,
        accountPartyContactDetailsFetchedAt: state.communication.accountPartyContactDetailsFetchedAt,
        serviceCaseInvoice: createServiceCaseInvoiceSelector(state, props),
        activeUser: userProfileSelectors.getUser(state),
        selectedSearchContact: getSelectedContact(state, props),
        persistenceState: state.communication.persistenceState,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        confirmSendMessage: payload => {
            dispatch({
                type: communicationActionTypes.CONFIRM_SEND_MESSAGE,
                payload,
            });
        },
        fetchAccountPartyDetails: payload => {
            dispatch({
                type: communicationActionTypes.FETCH_ACCOUNT_PARTY_CONTACT_DETAILS,
                payload,
            });
        },
        resetAccountPartyContactDetails: () => {
            dispatch({
                type: communicationActionTypes.RESET_ACCOUNT_PARTY_CONTACT_DETAILS,
            });
        },
        createTask: payload => {
            dispatch({
                type: taskActionTypes.CREATE_TASK,
                payload,
            });
        },
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SendMessageContent));
